/**
 * InitialReviewModal
 *
 * Fullscreen modal for conducting the initial case review.
 * Two-column layout: left scrollable work area, right contextual panel.
 */
import { ExternalLink } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import {
  INITIAL_REVIEW_EVENTS,
  INITIAL_REVIEW_PROPERTIES,
} from '@/constants/analytics/initial-review-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { useGetFirmQuery } from '@/services/api/firmsService';
import {
  useGetMatterDetailsQuery,
  usePatchMatterMutation,
} from '@/services/api/mattersService';
import { useCreateStaffNoteMutation } from '@/services/api/staffNotesService';
import {
  useCreateTaskUpdateMutation,
  useGetTasksQuery,
  useGetTaskStatusesQuery,
  useUpdateTaskMutation,
} from '@/services/api/tasksService';
import {
  MatterStatus,
  TaskUpdateVisibility,
} from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

import { CaseContextSection } from './components/CaseContextSection';
import { CaseIngestionResultsSection } from './components/CaseIngestionResultsSection';
import { DocumentLibrarySection } from './components/DocumentLibrarySection';
import { DocumentPreviewPanel } from './components/DocumentPreviewPanel';
import { FullDocumentViewer } from './components/FullDocumentViewer';
import { FullTaskViewer } from './components/FullTaskViewer';
import { ReviewConfigStrip } from './components/ReviewConfigStrip';
import { ScopeOfWorkSection } from './components/ScopeOfWorkSection';
import { TaskReviewSection } from './components/TaskReviewSection';
import { WelcomeCallPrep } from './components/WelcomeCallPrep';
import { useInitialReviewState } from './hooks/useInitialReviewState';
import type { InitialReviewModalProps } from './types';

/** Dialog state types for confirmation dialogs */
type ConfirmDialogState =
  | { type: 'none' }
  | {
      type: 'confirm';
      hasNoCallPrep: boolean;
    };

export const InitialReviewModal = ({
  open,
  onOpenChange,
  matterId,
  taskId,
}: InitialReviewModalProps) => {
  const [isCompleting, setIsCompleting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    type: 'none',
  });
  const [highlightBillingCode, setHighlightBillingCode] = useState(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const capture = usePostHogCapture();
  const modalOpenedAtRef = useRef<number | null>(null);

  // Track when modal opens for time-to-complete calculation
  useEffect(() => {
    if (open && !modalOpenedAtRef.current) {
      modalOpenedAtRef.current = Date.now();
    } else if (!open) {
      modalOpenedAtRef.current = null;
    }
  }, [open]);

  // Cleanup highlight timeout on unmount to avoid setting state on unmounted component
  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // Fetch matter details
  const { data: matter, isLoading: isMatterLoading } = useGetMatterDetailsQuery(
    matterId,
    { skip: !open || !matterId },
  );

  // Fetch firm data to check feature flags
  const { data: firmData } = useGetFirmQuery(matter?.firm?.id ?? '', {
    skip: !open || !matter?.firm?.id,
  });

  // Check if matter workflow overrides feature is enabled (hides scope of work)
  const isMatterWorkflowsEnabled =
    firmData?.feature_flags?.matter_workflow_overrides === 'true';

  // Track contact_client_directly state (defaults to matter value)
  const canContactClient = matter?.contact_client_directly !== false;


  // Fetch tasks for this matter to find welcome call task
  const { data: tasksData } = useGetTasksQuery(
    { matter: matterId },
    { skip: !open || !matterId },
  );

  // Fetch task statuses to get the completed status ID
  const { data: taskStatuses = [] } = useGetTaskStatusesQuery();

  // Find the completed status
  const completedStatus = useMemo(() => {
    return taskStatuses.find(
      (s) => s.canonical_status === CanonicalTaskStatus.COMPLETED,
    );
  }, [taskStatuses]);

  // Mutations
  const [updateTask] = useUpdateTaskMutation();
  const [createTaskUpdate] = useCreateTaskUpdateMutation();
  const [patchMatter] = usePatchMatterMutation();
  const [createStaffNote] = useCreateStaffNoteMutation();

  // Handle toggle of contact_client_directly
  const handleContactClientToggle = useCallback(
    async (checked: boolean) => {
      try {
        await patchMatter({
          matter_id: matterId,
          patch: { contact_client_directly: checked },
        }).unwrap();
        toast.success(
          checked
            ? 'Client contact enabled'
            : 'Client contact disabled - notes will go to initial review task',
        );
      } catch {
        toast.error('Failed to update client contact setting');
      }
    },
    [matterId, patchMatter],
  );

  // Find the welcome call task
  const welcomeCallTask = useMemo(() => {
    if (!tasksData?.results) return null;
    return tasksData.results.find(
      (task) =>
        task.type?.canonical_type === CanonicalTaskType.CONDUCT_INTAKE ||
        task.name?.toLowerCase().includes('welcome call'),
    );
  }, [tasksData?.results]);

  // Initialize state hook with matterId for localStorage persistence
  const reviewState = useInitialReviewState({ matterId });

  // Find any unassigned tasks
  const unassignedTasks = useMemo(() => {
    if (!tasksData?.results) return [];
    return tasksData.results.filter(
      (task) =>
        !task.assignee &&
        task.status?.canonical_status !== CanonicalTaskStatus.COMPLETED &&
        task.status?.canonical_status !== CanonicalTaskStatus.NOT_APPLICABLE,
    );
  }, [tasksData?.results]);

  // Execute the actual completion logic
  const executeComplete = useCallback(async () => {
    setIsCompleting(true);

    try {
      const callPrepText = reviewState.getFormattedCallPrep();

      // 1. Save notes with cascade: Welcome Call → Initial Review → Staff Note
      if (callPrepText.trim()) {
        if (welcomeCallTask) {
          // First priority: save to welcome call task
          await createTaskUpdate({
            matterId,
            taskId: welcomeCallTask.id,
            update: {
              update: `**Initial Review Prep Notes:**\n\n${callPrepText}`,
              visibility: TaskUpdateVisibility.PRIVATE,
            },
          }).unwrap();
        } else if (taskId) {
          // Second priority: save to initial review task
          await createTaskUpdate({
            matterId,
            taskId,
            update: {
              update: `**Initial Review Prep Notes:**\n\n${callPrepText}`,
              visibility: TaskUpdateVisibility.PRIVATE,
            },
          }).unwrap();
        } else {
          // Fallback: save as staff note on the matter
          await createStaffNote({
            matterId,
            note: {
              content: `**Initial Review Prep Notes:**\n\n${callPrepText}`,
            },
          }).unwrap();
        }
      }

      // 2. Complete the initial review task (if it exists)
      if (taskId && completedStatus) {
        await updateTask({
          matterId,
          taskId,
          task: {
            status_id: completedStatus.id,
            completed_at: new Date().toISOString(),
            skip_attachment_validation: true,
          },
        }).unwrap();
      }

      // 3. Always move matter to Case Setup status
      await patchMatter({
        matter_id: matterId,
        patch: { status: MatterStatus.CASE_SETUP },
      }).unwrap();

      // 4. Track completion analytics
      const timeToComplete = modalOpenedAtRef.current
        ? Math.round((Date.now() - modalOpenedAtRef.current) / 1000)
        : undefined;

      capture(INITIAL_REVIEW_EVENTS.REVIEW_COMPLETED, {
        [INITIAL_REVIEW_PROPERTIES.MATTER_ID]: matterId,
        [INITIAL_REVIEW_PROPERTIES.TASK_ID]: taskId,
        [INITIAL_REVIEW_PROPERTIES.CALL_PREP_ITEM_COUNT]:
          reviewState.callPrepItems.length,
        [INITIAL_REVIEW_PROPERTIES.CAN_CONTACT_CLIENT]: canContactClient,
        [INITIAL_REVIEW_PROPERTIES.TIME_TO_COMPLETE_SECONDS]: timeToComplete,
      });

      // Clear persisted draft notes after successful completion
      reviewState.clearPersistedNotes();

      toast.success('Initial review completed');
      onOpenChange(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to complete review:', error);
      toast.error('Failed to complete review. Please try again.');
    } finally {
      setIsCompleting(false);
    }
  }, [
    completedStatus,
    canContactClient,
    welcomeCallTask,
    reviewState,
    matterId,
    taskId,
    createTaskUpdate,
    createStaffNote,
    updateTask,
    patchMatter,
    onOpenChange,
    capture,
  ]);

  // Handle complete review button click
  const handleCompleteReview = useCallback(() => {
    if (!matter?.simplified_billing_code) {
      setHighlightBillingCode(true);
      toast.warning('Billing code is required. Please ask the Account Executive in Slack if you are unsure on the billing arrangement for the firm.', {
        duration: 6000,
      });
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = setTimeout(() => setHighlightBillingCode(false), 3000);
      return;
    }

    // Validation: Check for unassigned tasks
    if (unassignedTasks.length > 0) {
      toast.error(
        `${unassignedTasks.length} task(s) need an assignee before completing`,
      );
      return;
    }

    const hasNoCallPrep = !reviewState.hasCallPrepItems;

    // Show confirmation if no call prep items
    if (hasNoCallPrep) {
      setConfirmDialog({
        type: 'confirm',
        hasNoCallPrep,
      });
      return;
    }

    // Otherwise proceed directly
    executeComplete();
  }, [
    unassignedTasks.length,
    matter?.simplified_billing_code,
    reviewState.hasCallPrepItems,
    executeComplete,
  ]);

  // Handle close - notes are auto-saved to localStorage
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Handle confirmation dialog actions
  const handleConfirmDialogAction = useCallback(() => {
    setConfirmDialog({ type: 'none' });
    executeComplete();
  }, [executeComplete]);

  if (!open) return null;

  // If in document view mode, show full document viewer
  if (reviewState.modalViewMode.mode === 'document') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="flex h-[95vh] w-[95vw] max-w-none flex-col overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-0 shadow-xl [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <FullDocumentViewer
            artifactId={reviewState.modalViewMode.artifactId}
            matterId={matterId}
            onBack={reviewState.showReviewView}
            onNavigate={reviewState.showFullDocumentView}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // If in task view mode, show full task detail
  if (reviewState.modalViewMode.mode === 'task') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="flex h-[95vh] w-[95vw] max-w-none flex-col overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-0 shadow-xl [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <FullTaskViewer
            taskId={reviewState.modalViewMode.taskId}
            onBack={reviewState.showReviewView}
          />
        </DialogContent>
      </Dialog>
    );
  }

  // Main review view
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="flex h-[95vh] w-[95vw] max-w-none flex-col overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-0 shadow-xl [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Header */}
        <DialogHeader className="flex-shrink-0 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-base font-semibold text-zinc-900 dark:text-white">
                {matter?.name || 'Loading...'}
              </DialogTitle>
              {matter?.intake_id ? (
                <a
                  href={`/intakes/${matter.intake_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-md bg-blue-50 dark:bg-blue-900/20 px-2.5 py-1 text-xs font-medium text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  Finch Intake
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              ) : (
                <span className="inline-flex items-center rounded-md bg-zinc-100 dark:bg-zinc-800 px-2.5 py-1 text-xs font-medium text-zinc-700 dark:text-zinc-300">
                  Firm Assigned
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleClose}
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-sm font-medium text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                Close
              </Button>
              <Button
                onClick={handleCompleteReview}
                disabled={isCompleting || isMatterLoading}
                size="sm"
                className="h-8 bg-indigo-600 px-4 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCompleting ? 'Completing...' : 'Complete Review'}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Config Strip - matter-level settings */}
        <ReviewConfigStrip
          matter={matter}
          matterId={matterId}
          canContactClient={canContactClient}
          onContactClientChange={handleContactClientToggle}
          isContactToggleDisabled={isMatterLoading}
          highlightBillingCode={highlightBillingCode}
        />

        {/* Main content - three column layout with resizable panels */}
        <ResizablePanelGroup direction="horizontal" className="min-h-0 flex-1">
          {/* Left column - compact reference information */}
          <ResizablePanel defaultSize={22} minSize={15} maxSize={35}>
            <div className="flex h-full flex-col overflow-y-auto border-r border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              {isMatterLoading ? (
                <div className="flex flex-1 items-center justify-center p-4">
                  <div className="h-6 w-6 animate-spin rounded-full border-3 border-zinc-200 dark:border-zinc-700 border-t-indigo-600 dark:border-t-indigo-400" />
                </div>
              ) : (
                <div className="p-4 space-y-3">
                  <div className="mb-2">
                    <h2 className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wide">
                      Case Reference
                    </h2>
                  </div>
                  {/* Case Context Section - Compact Version */}
                  <CaseContextSection
                    matter={matter}
                    matterId={matterId}
                    toggleInCallPrep={reviewState.toggleContextInCallPrep}
                    isInCallPrep={reviewState.isInCallPrep}
                  />

                  {/* Scope of Work Section - hidden when matter_workflow_overrides is enabled */}
                  {!isMatterWorkflowsEnabled && (
                    <ScopeOfWorkSection firmId={matter?.firm?.id} />
                  )}
                </div>
              )}
            </div>
          </ResizablePanel>

          <ResizableHandle
            withHandle
            className="bg-zinc-200 dark:bg-zinc-800 hover:bg-indigo-500 dark:hover:bg-indigo-500 transition-colors"
          />

          {/* Middle column - main work area */}
          <ResizablePanel defaultSize={48} minSize={35} maxSize={65}>
            <div className="flex h-full flex-col overflow-y-auto bg-zinc-50 dark:bg-zinc-950">
              {isMatterLoading ? (
                <div className="flex flex-1 items-center justify-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-zinc-200 dark:border-zinc-700 border-t-indigo-600 dark:border-t-indigo-400" />
                    <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
                      Loading case details...
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 p-6">
                  {/* Case Ingestion Results - if ingestion record exists */}
                  <CaseIngestionResultsSection
                    matterId={matterId}
                    onDocumentClick={reviewState.showFullDocumentView}
                  />

                  {/* Document Library Section - browse all matter docs */}
                  <DocumentLibrarySection
                    matterId={matterId}
                    onDocumentClick={reviewState.showFullDocumentView}
                    isOpen={reviewState.isDocumentLibraryOpen}
                    onOpenChange={reviewState.setIsDocumentLibraryOpen}
                  />

                  {/* Task Review Section */}
                  <TaskReviewSection
                    matterId={matterId}
                    onTaskClick={reviewState.showTaskView}
                    onDocumentClick={reviewState.showFullDocumentView}
                    toggleInCallPrep={reviewState.toggleContextInCallPrep}
                    isInCallPrep={reviewState.isInCallPrep}
                  />
                </div>
              )}
            </div>
          </ResizablePanel>

          <ResizableHandle
            withHandle
            className="bg-zinc-200 dark:bg-zinc-800 hover:bg-indigo-500 dark:hover:bg-indigo-500 transition-colors"
          />

          {/* Right column - call prep / follow up panel */}
          <ResizablePanel defaultSize={30} minSize={20} maxSize={45}>
            <div className="flex h-full flex-col border-l border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              {reviewState.rightPanelMode.mode === 'callPrep' ? (
                <div className="flex h-full flex-col">
                  <div className="shrink-0 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 px-3 py-2">
                    <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                      {canContactClient ? 'Call Prep' : 'Follow Up'}
                    </span>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    <WelcomeCallPrep
                      items={reviewState.callPrepItems}
                      onRemoveItem={reviewState.removeCallPrepItem}
                      customNote={reviewState.customNote}
                      onCustomNoteChange={reviewState.setCustomNote}
                      getFormattedText={reviewState.getFormattedCallPrep}
                      canContactClient={canContactClient}
                    />
                  </div>
                </div>
              ) : (
                <DocumentPreviewPanel
                  artifactId={reviewState.rightPanelMode.artifactId}
                  matterId={matterId}
                  onBack={reviewState.showCallPrep}
                />
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </DialogContent>

      {/* Confirmation Dialog */}
      <AlertDialog
        open={confirmDialog.type === 'confirm'}
        onOpenChange={(isOpen) => !isOpen && setConfirmDialog({ type: 'none' })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Complete review?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-zinc-500">
                {confirmDialog.type === 'confirm' &&
                  confirmDialog.hasNoCallPrep && (
                    <p>No call prep notes have been added.</p>
                  )}
                {confirmDialog.type === 'confirm' &&
                  !confirmDialog.hasNoCallPrep && (
                    <p>Are you sure you want to complete this review?</p>
                  )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDialogAction}>
              Complete review
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
